import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useLocalStorage, useSessionStorage, useStorage } from '@vueuse/core';
import { refresh } from '#modules/auth/services/AuthService.js';
import { hydrate } from '@/app/services/hydrate.js';
import { useAuthStore } from '#modules/auth/stores/auth.store.js';

export const useAppStore = defineStore('app', () => {
    const initializing = ref(true); // This indicates that the app is in the process of initializing
    const hydrated = ref(false); // This indicates that the app is ready to be rendered
    const hydrating = ref(false);
    const error = ref(null);

    const showMigrationHelp = useLocalStorage('showMigrationHelp', true);

    const showSearchModal = ref(false);
    const searchQuery = ref('');

    const sidebarOpen = useStorage('sidebarOpen', false); // For mobile
    const sidebarExpanded = useStorage('sidebarExpanded', true); // For desktop
    const contentSidebarOpen = useStorage('contentSidebarOpen', false); // For mobile

    // Store previous route for navigation out of app-sidebar layout
    const previousRoute = useSessionStorage('cyberfusion.previousRoute', null);

    const setPreviousRoute = (route) => {
        previousRoute.value = route;
    };

    const setError = (err) => {
        error.value = err;
    };

    const initialize = async () => {
        const authStore = useAuthStore();

        // Check if access token is expired
        if (authStore.accessToken && authStore.accessTokenExpiry < Date.now()) {
            // Try retrieving a fresh access token on first load
            try {
                await refresh({ navigate: false });
            } catch {
                // Ignore error
            }
        }

        // Hydrate application state if token is present
        if (authStore.accessToken) {
            try {
                await hydrate();
            } catch {
                // hydrate() already set appStore.error; initialization must still complete
            }
        }

        // Ensure minimum loading time for better UX (loading spinner)
        setTimeout(() => {
            initializing.value = false;
        }, 500);
    };

    return {
        initializing,
        hydrated,
        hydrating,
        error,
        showMigrationHelp,
        showSearchModal,
        searchQuery,
        sidebarOpen,
        sidebarExpanded,
        contentSidebarOpen,
        previousRoute,
        setPreviousRoute,
        setError,
        initialize,
    };
});
